import L from "leaflet";

// A small gas-pump silhouette (cabinet + fuel-drop accent + hose/nozzle)
// plus a colored bar beside it -- the icon gives instant "this is a fuel
// stop" recognition, the bar carries the green-to-red price gradient so
// the price signal isn't lost by moving off a plain colored dot. The
// cabinet front uses a fuel-drop accent rather than a rectangular
// display -- an earlier version paired a screen-like rectangle with two
// horizontal bars underneath, which read as a calculator rather than a
// pump at map-marker size.
export function stationIcon(color, { size = 24, ringColor = null, pulse = false, label = null, labelColor = "#f2a33d" } = {}) {
  const ring = ringColor || "#0b1020";
  const ringWidth = ringColor ? 2.5 : 1;
  const barWidth = ringColor ? 8 : 6;
  // The recommended stop needs to be unmissable against a map full of
  // identical-looking pump icons, not just slightly bigger -- an animated
  // glow ring is what actually catches the eye at a glance (reserved for
  // the single "fill up here next" recommendation, not every route-plan
  // stop, so it doesn't turn into visual noise). The label is baked
  // directly into the icon HTML (not a separate react-leaflet `permanent`
  // Tooltip) because that component was unreliable here -- this way it's
  // guaranteed to render exactly when the icon does.
  const pulseStyle = pulse
    ? "animation: sipFuelPulse 1.6s ease-out infinite; border-radius: 10px;"
    : "";
  const pulseKeyframes = pulse
    ? `<style>@keyframes sipFuelPulse {
        0% { box-shadow: 0 0 0 0 ${ring}a6; }
        70% { box-shadow: 0 0 0 16px ${ring}00; }
        100% { box-shadow: 0 0 0 0 ${ring}00; }
      }</style>`
    : "";
  const labelHtml = label
    ? `<div style="
        position:absolute; bottom:100%; left:50%; transform:translateX(-50%);
        margin-bottom:6px; white-space:nowrap; background:${labelColor}; color:#0b1020;
        font-size:10.5px; font-weight:800; letter-spacing:.02em; padding:3px 7px;
        border-radius:5px; box-shadow:0 2px 6px rgba(0,0,0,.5); pointer-events:none;
      ">${label}</div>`
    : "";
  const html = `
    ${pulseKeyframes}
    <div style="position:relative; display:flex; align-items:center; gap:3px; pointer-events:none; padding:3px; ${pulseStyle}">
      ${labelHtml}
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.5));">
        <rect x="3" y="6" width="10" height="14" rx="1.5" fill="#2a303b" stroke="${ring}" stroke-width="${ringWidth}"/>
        <rect x="2" y="20" width="12" height="1.6" rx="0.8" fill="${ring}"/>
        <path d="M8 9c1.3 1.6 2 2.6 2 3.3a2 2 0 1 1-4 0c0-.7.7-1.7 2-3.3z" fill="#7fd3d3"/>
        <path d="M13 11h2.2a1.8 1.8 0 0 1 1.8 1.8v5.7a1.25 1.25 0 0 0 2.5 0V8.6" stroke="#eef0f2" stroke-width="1.6" stroke-linecap="round" fill="none"/>
        <path d="M16.3 8.6h3" stroke="#eef0f2" stroke-width="1.6" stroke-linecap="round"/>
        <circle cx="19.3" cy="7.2" r="1.3" fill="none" stroke="#eef0f2" stroke-width="1.4"/>
      </svg>
      <div style="width:${barWidth}px; height:${size}px; background:${color}; border-radius:2px; border:1px solid #0b1020; box-shadow:0 1px 3px rgba(0,0,0,.4);"></div>
    </div>`;
  const w = size + barWidth + 6 + (ringColor ? 6 : 0);
  return L.divIcon({ html, className: "", iconSize: [w, size], iconAnchor: [w / 2, size / 2] });
}
